// src/pages/InventoryPage.js
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
 XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';

import Select from 'react-select'; 

// Base API URL
const API_URL = process.env.REACT_APP_API_URL;

const InventoryPage = () => {


    const [summary, setSummary] = useState(null);
    const [products, setProducts] = useState([]);
    const [movementData, setMovementData] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [isInputModalOpen, setIsInputModalOpen] = useState(false);
    const [isOutputModalOpen, setIsOutputModalOpen] = useState(false);
    const [inputQuantity, setInputQuantity] = useState('');
    const [outputQuantity, setOutputQuantity] = useState('');
    const [selectedProductOption, setSelectedProductOption] = useState(null);
    const productOptions = products.map(p => ({
        value: p.productId,
        label: p.productName
    }));
    useEffect(() => {
        fetchInventorySummary();
        fetchProducts();
    }, []);

    useEffect(() => {
        if (products.length > 0 && selectedProductId === null) {
            setSelectedProductId(products[0].productId);
            setSelectedProductOption({ value: products[0].productId, label: products[0].productName });
        }
    }, [products]);

    const handleProductSelectChange = (selectedOption) => {
        setSelectedProductOption(selectedOption);
        setSelectedProductId(selectedOption ? selectedOption.value : null);
    };

    useEffect(() => {
        if (selectedProductId) {
            fetchProductMovements(selectedProductId);
        }
    }, [selectedProductId]);

    // Fetches overall inventory stats
    const fetchInventorySummary = async () => {
        console.log('Attempting to fetch inventory summary from:', `${API_URL}/inventory/summary`);

        try {
            const response = await fetch(`${API_URL}/inventory/summary`);
            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`API response was not OK: ${response.status} ${response.statusText}`);
                console.error('Raw error response:', errorText);
                return;
            }

            const data = await response.json();
            console.log('Successfully fetched and parsed data:', data);

            // Convert string values to numbers
            const summaryData = {
                totalStock: Number(data.totalStock),
                totalInputs: Number(data.totalInputs),
                totalOutputs: Number(data.totalOutputs)
            };

            // Now, set the state with the corrected numerical values
            if (summaryData && typeof summaryData.totalStock === 'number') {
                setSummary(summaryData);
                console.log('Summary state has been updated.');
            } else {
                console.error('Data received is not in the expected format:', data);
            }

        } catch (error) {
            console.error('A network or parsing error occurred during fetch:', error);
        }
    };

    // Fetches a list of all products for the dropdown
    const fetchProducts = async () => {
        try {
            const response = await fetch(`${API_URL}/products`);

            if (!response.ok) {
                console.error(`Failed to fetch products: ${response.status} ${response.statusText}`);
                return;
            }

            const data = await response.json();

            if (data && Array.isArray(data.products)) {
                // Map over the products, converting stock_quantity to a number.
                // Use '|| 0' to handle cases where the value is null, undefined, or an empty string.
                const productsWithNumberStock = data.products.map(p => ({
                    ...p,
                    stock_quantity: Number(p.stock_quantity) || 0
                }));

                setProducts(productsWithNumberStock);

                if (productsWithNumberStock.length > 0) {
                    setSelectedProductId(productsWithNumberStock[0].productId);
                }
            } else {
                console.error('API response is not in the expected format:', data);
                setProducts([]);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            setProducts([]);
        }
    };

    // Fetches movements for the selected product
    const fetchProductMovements = async (productId) => {
        try {
            const response = await fetch(`${API_URL}/inventory/products/${productId}/movements`);
            const data = await response.json();
            setMovementData(data.movements);
        } catch (error) {
            console.error('Failed to fetch product movements', error);
        }
    };

    const handleInput = async (e) => {
        e.preventDefault();
        try {
            await fetch(`${API_URL}/inventory/input`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productId: selectedProductId, quantity: parseInt(inputQuantity) })
            });
            fetchInventorySummary();
            fetchProductMovements(selectedProductId);
            setIsInputModalOpen(false);
            setInputQuantity('');
        } catch (error) {
            console.error('Failed to add input', error);
        }
    };

    const handleOutput = async (e) => {
        e.preventDefault();
        try {
            await fetch(`${API_URL}/inventory/output`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productId: selectedProductId, quantity: parseInt(outputQuantity) })
            });
            fetchInventorySummary();
            fetchProductMovements(selectedProductId);
            setIsOutputModalOpen(false);
            setOutputQuantity('');
        } catch (error) {
            console.error('Failed to add output', error);
        }
    };

    const productNames = products.reduce((acc, p) => ({ ...acc, [p.productId]: p.productName }), {});

    const pieData = [
        { name: 'Entrées', value: summary?.totalInputs || 0, fill: '#8884d8' },
        { name: 'Sorties', value: summary?.totalOutputs || 0, fill: '#82ca9d' },
    ];

    return (
        <div className="p-8 bg-gray-100 min-h-screen">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Gestion des Stocks 📈</h1>
            <p className="text-gray-600 mb-8">
                Visualisez l'état de votre inventaire, suivez les entrées et les sorties, et gérez les niveaux de stock.
            </p>

            {/* Inventory Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-lg font-semibold text-gray-700">Stock Total</h2>
                    <p className="text-3xl font-bold text-indigo-600 mt-2">{summary?.totalStock || '...'}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-lg font-semibold text-gray-700">Total des Entrées</h2>
                    <p className="text-3xl font-bold text-green-600 mt-2">{summary?.totalInputs || '...'}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-lg font-semibold text-gray-700">Total des Sorties</h2>
                    <p className="text-3xl font-bold text-red-600 mt-2">{summary?.totalOutputs || '...'}</p>
                </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Stock Movement Chart */}
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Mouvements de Stock</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} label>
                                {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} />)}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Stock Level Trend Chart */}
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Stock Par Produit</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={products}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="productName" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="stockQuantity" fill="#8884d8" name="Stock Restant" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Management Section */}
            <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Gestion des Entrées/Sorties</h2>
                <div className="flex items-center space-x-4 mb-6">
                    <label htmlFor="product-select" className="text-gray-700 font-medium">Sélectionnez un produit:</label>
                    <Select
                        options={productOptions}
                        value={selectedProductOption}
                        onChange={handleProductSelectChange}
                        placeholder="Rechercher un produit..."
                        isClearable
                        noOptionsMessage={() => "Aucun produit trouvé."}
                        styles={{
                            control: (baseStyles) => ({
                                ...baseStyles,
                                borderRadius: '0.5rem',
                                borderColor: '#e5e7eb',
                                boxShadow: 'none',
                                '&:hover': {
                                    borderColor: '#a5b4fc',
                                },
                            }),
                        }}
                    />
                </div>
                <div className="flex space-x-4">
                    <button
                        onClick={() => setIsInputModalOpen(true)}
                        className="px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                    >
                        Ajouter du Stock
                    </button>
                    <button
                        onClick={() => setIsOutputModalOpen(true)}
                        className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                    >
                        Retirer du Stock
                    </button>
                </div>
            </div>

            {/* Movement History Table */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Historique des Mouvements</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full table-auto">
                        <thead>
                            <tr className="bg-gray-50 text-gray-600 uppercase text-sm font-semibold">
                                <th className="py-3 px-6 text-left">Date</th>
                                <th className="py-3 px-6 text-left">Produit</th>
                                <th className="py-3 px-6 text-left">Type de Mouvement</th>
                                <th className="py-3 px-6 text-right">Quantité</th>
                            </tr>
                        </thead>
                        <tbody className="text-gray-600 text-sm font-light">
                            {movementData.length > 0 ? (
                                movementData.map(move => (
                                    <tr key={move.movementId} className="border-b border-gray-200 hover:bg-gray-50">
                                        <td className="py-3 px-6">{new Date(move.movementDate).toLocaleString()}</td>
                                        <td className="py-3 px-6">{productNames[move.productId]}</td>
                                        <td className="py-3 px-6">
                                            <span className={`py-1 px-3 rounded-full text-xs font-medium ${move.movementType === 'input' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                                                }`}>
                                                {move.movementType === 'input' ? 'Entrée' : 'Sortie'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-6 text-right">{move.quantity}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="py-10 text-center text-gray-500 italic">Aucun mouvement de stock pour ce produit.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Input Modal */}
            {isInputModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center">
                    <div className="bg-white p-6 rounded-xl shadow-lg w-96">
                        <h3 className="text-xl font-semibold mb-4">Ajouter du Stock</h3>
                        <form onSubmit={handleInput}>
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2">Quantité:</label>
                                <input
                                    type="number"
                                    value={inputQuantity}
                                    onChange={(e) => setInputQuantity(e.target.value)}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    required
                                />
                            </div>
                            <div className="flex justify-end space-x-4">
                                <button type="button" onClick={() => setIsInputModalOpen(false)} className="px-4 py-2 bg-gray-300 rounded-md">Annuler</button>
                                <button type="submit" className="px-4 py-2 bg-green-500 text-white rounded-md">Confirmer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Output Modal */}
            {isOutputModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center">
                    <div className="bg-white p-6 rounded-xl shadow-lg w-96">
                        <h3 className="text-xl font-semibold mb-4">Retirer du Stock</h3>
                        <form onSubmit={handleOutput}>
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2">Quantité:</label>
                                <input
                                    type="number"
                                    value={outputQuantity}
                                    onChange={(e) => setOutputQuantity(e.target.value)}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    required
                                />
                            </div>
                            <div className="flex justify-end space-x-4">
                                <button type="button" onClick={() => setIsOutputModalOpen(false)} className="px-4 py-2 bg-gray-300 rounded-md">Annuler</button>
                                <button type="submit" className="px-4 py-2 bg-red-500 text-white rounded-md">Confirmer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryPage;